const MINUTE = 60_000
const DAY = 86_400_000

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
}

export function formatClock(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
}

/** Compact relative time for the conversation list. */
export function formatRelativeTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return ''
  const date = new Date(iso)
  const diff = now.getTime() - date.getTime()
  if (Number.isNaN(diff)) return ''
  if (diff < MINUTE) return 'now'
  if (diff < 60 * MINUTE) return `${Math.floor(diff / MINUTE)}m`
  const days = Math.round((startOfDay(now) - startOfDay(date)) / DAY)
  if (days <= 0) return formatClock(iso)
  if (days === 1) return 'Yesterday'
  if (days < 7) return date.toLocaleDateString('en-GB', { weekday: 'short' })
  return formatShortDate(iso, now)
}

/** When a search hit was written: the clock today, otherwise the day as the chat list writes it, then the clock ("Sat 23:57"). */
export function formatHitTime(iso: string, now: Date = new Date()): string {
  const date = new Date(iso)
  const days = Math.round((startOfDay(now) - startOfDay(date)) / DAY)
  if (days <= 0) return formatClock(iso)
  const day = days === 1 ? 'Yesterday' : days < 7 ? date.toLocaleDateString('en-GB', { weekday: 'short' }) : formatShortDate(iso, now)
  return `${day} ${formatClock(iso)}`
}

/** A day, e.g. "14 Sept", with the year when it isn't this year ("14 Sept 2025"). */
export function formatShortDate(iso: string, now: Date = new Date()): string {
  const date = new Date(iso)
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }) })
}

/** Day label for dividers in the conversation. */
export function formatDayLabel(iso: string, now: Date = new Date()): string {
  const date = new Date(iso)
  const days = Math.round((startOfDay(now) - startOfDay(date)) / DAY)
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  return date.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  })
}

export function dayKey(iso: string): string {
  const d = new Date(iso)
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function fileTypeLabel(fileName: string): string {
  const parts = fileName.split('.')
  return (parts.length > 1 ? parts.pop()! : 'FILE').toUpperCase().slice(0, 5)
}

export function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .map((n) => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?'
  )
}

export const avatarTones = ['blue', 'slate', 'sand', 'purple', 'mint'] as const
export type AvatarTone = (typeof avatarTones)[number]

export function toneFor(id: string): AvatarTone {
  let hash = 0
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0
  return avatarTones[Math.abs(hash) % avatarTones.length]
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`
}
